import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Modal,
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Upload,
  Select,
  DatePicker,
  Switch,
  Collapse,
  message,
  Divider,
  Typography,
  Image,
  Tag,
  Space,
  Table,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid"; // For generating unique keys
import _, { isEmpty, isNil } from "lodash";
import moment from "moment";

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;
const { Title } = Typography;
const REWARD_CATEGORIES = ["bon d'achat", "réduction", "service", "produit"];

const CampaignTable = ({ campagnes, onEdit, onDelete, handleAddCampaign }) => {
  const [filterType, setFilterType] = useState(null);

  const filteredData = filterType
    ? campagnes.filter((c) => c.type === filterType)
    : campagnes;

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: [
        { text: "Instagram", value: "Instagram" },
        { text: "TikTok", value: "TikTok" },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Date Début",
      dataIndex: "dateDebut",
      key: "dateDebut",
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
    },
    {
      title: "Date Fin",
      dataIndex: "dateFin",
      key: "dateFin",
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
    },
    {
      title: "Budget",
      dataIndex: "budget",
      key: "budget",
      render: (text) => `${text} €`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) =>
        moment().isAfter(dayjs(record.dateFin).format("DD/MM/YYYY"))
          ? "Archiver"
          : `${text}`,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record, index) => (
        <Space>
          <Button onClick={() => onEdit(record, index)}>Modifier</Button>
          <Button danger onClick={() => onDelete(index)}>
            Supprimer
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between">
        <Col>
          <Select
            placeholder="Filtrer par type"
            style={{ width: 200 }}
            onChange={setFilterType}
            allowClear
          >
            <Option value="Instagram">Instagram</Option>
            <Option value="TikTok">TikTok</Option>
          </Select>
        </Col>
        <Col>
          <Button type="primary" onClick={handleAddCampaign}>
            Ajouter une Campagne
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="id"
        pagination={false}
      />
    </>
  );
};

// Modified EtablissementModal component
const EtablissementModal = ({
  visible,
  onCancel,
  record,
  actionType,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [campaignModalVisible, setCampaignModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignIndex, setCampaignIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (actionType === "EDIT" && record) {
      const campagnesWithDates =
        record.campagnes?.map((campagne) => ({
          ...campagne,
          dateDebut: campagne.dateDebut ? moment(campagne.dateDebut) : null,
          dateFin: campagne.dateFin ? moment(campagne.dateFin) : null,
        })) || [];

      form.setFieldsValue({
        ...record,
        dateDebut: record.dateDebut ? moment(record.dateDebut) : null,
        dateFin: record.dateFin ? moment(record.dateFin) : null,
        coordinate: record.coordinate
          ? {
              latitude: parseFloat(record.coordinate.latitude),
              longitude: parseFloat(record.coordinate.longitude),
            }
          : null,
        multimedia: record.multimedia || [],
        campagnes: campagnesWithDates,
      });
    } else {
      form.resetFields();
    }
  }, [form, record, visible, actionType]);

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Vous ne pouvez télécharger que des fichiers image !");
    }
    return isImage;
  };

  const handleFileUpload = async (options, fieldName) => {
    const { file, onSuccess, onError } = options;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        "https://trend-me.com/upload",
        formData
      );
      const filename = response.data.filename;
      const url = `https://trend-me.com/upload/${filename}`;

      // ALWAYS notify AntD that the upload succeeded
      onSuccess && onSuccess(response.data, file);

      if (fieldName !== "multimedia") {
        // Only set form values for avatar / couverture here
        form.setFieldsValue({ [fieldName]: filename });
      }
      // ← REMOVE anything that manually updates `multimedia`
    } catch (err) {
      onError && onError(err);
      message.error("Échec du téléchargement du fichier");
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        campagnes: form.getFieldValue("campagnes")?.map((c) => ({
          ...c,
          dateDebut: dayjs(c.dateDebut).format("YYYY-MM-DD"),
          dateFin: dayjs(c.dateFin).format("YYYY-MM-DD"),
        })),
      };

      if (actionType === "EDIT") {
        await axios.put(
          `https://trend-me.com/api/Etablissement/${record._id}`,
          payload
        );
        message.success("Établissement mis à jour avec succès");
      } else {
        await axios.post("https://trend-me.com/api/Etablissement", payload);
        message.success("Établissement créé avec succès");
      }

      onRefresh();
      onCancel();
    } catch (error) {
      console.log("testtttt", error);
      message.error("Opération échouée");
    } finally {
      setLoading(false);
    }
  };

  // Campaign management functions
  const handleAddCampaign = () => {
    setEditingCampaign(null);
    setCampaignIndex(-1);
    setCampaignModalVisible(true);
  };

  const handleEditCampaign = (campaign, index) => {
    setEditingCampaign(campaign);
    setCampaignIndex(index);
    setCampaignModalVisible(true);
  };

  const handleDeleteCampaign = (index) => {
    const campaigns = form.getFieldValue("campagnes") || [];
    form.setFieldsValue({
      campagnes: campaigns.filter((_, i) => i !== index),
    });
  };

  const handleCampaignSubmit = (values) => {
    const campaigns = form.getFieldValue("campagnes") || [];

    const inexistingFields = _.omit(editingCampaign, Object.keys(values));
    console.log("testttttttttttt", inexistingFields);

    const processedValues = {
      ...values,
      ...inexistingFields,
      dateDebut: values.dateDebut.format("YYYY-MM-DD"),
      dateFin: values.dateFin.format("YYYY-MM-DD"),
      recompenseOption: {
        ...values.recompenseOption,
        // Convert string points to numbers
        starterPoints: Number(values.recompenseOption.starterPoints),
        trendyPoints: Number(values.recompenseOption.trendyPoints),
        proPoints: Number(values.recompenseOption.proPoints),
        superStarPoints: Number(values.recompenseOption.superStarPoints),
      },
    };
    if (campaignIndex > -1) {
      campaigns[campaignIndex] = processedValues;
    } else {
      campaigns.push({ ...processedValues, id: uuidv4() });
    }

    form.setFieldsValue({ campagnes: [...campaigns] });
    setCampaignModalVisible(false);
  };

  return (
    <Modal
      title={`${actionType === "EDIT" ? "MODIFIER" : "CRÉER"} UN ÉTABLISSEMENT`}
      visible={visible}
      width={1400}
      onCancel={onCancel}
      confirmLoading={loading}
      onOk={() => form.submit()}
      okText={actionType === "EDIT" ? "Modifier" : "Créer"}
      cancelText="Annuler"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Collapse defaultActiveKey={["etablissement"]}>
          {/* Main Etablissement Section */}
          <Panel header="Etablissement" key="etablissement" forceRender>
            <Collapse defaultActiveKey={["infos-base"]}>
              {/* Informations de base */}
              <Panel header="Informations de base" key="infos-base" forceRender>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="title"
                      label="Titre dans la liste etablissement"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Nom de l'établissement"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="ville"
                      label="Ville"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="tag"
                      label="Tag"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="phone"
                      label="Téléphone"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="codePostal"
                      label="Code postal"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <TextArea rows={3} />
                </Form.Item>

                <Form.Item
                  name="cardDescription"
                  label="Description rapide pour la liste d'établissement"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input />
                </Form.Item>
              </Panel>

              {/* Localisation */}
              <Panel header="Localisation" key="localisation" forceRender>
                <Form.Item
                  name="location"
                  label="Adresse complète"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name={["coordinate", "latitude"]}
                      label="Latitude"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <InputNumber step="0.000001" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={["coordinate", "longitude"]}
                      label="Longitude"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <InputNumber step="0.000001" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Médias */}
              <Panel header="Médias" key="medias" forceRender>
                <Row gutter={16}>
                  {/* Avatar */}
                  <Col span={8}>
                    {/* 1) Upload button itself */}
                    <Form.Item
                      name="avatar"
                      label="Avatar"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Upload
                        customRequest={(option) =>
                          handleFileUpload(option, "avatar")
                        }
                        beforeUpload={beforeUpload}
                        showUploadList={false}
                      >
                        <Button>
                          <UploadOutlined />
                          Télécharger l’avatar
                        </Button>
                      </Upload>
                    </Form.Item>

                    {/* 2) Preview + little edit‐icon */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) => prev.avatar !== curr.avatar}
                    >
                      {({ getFieldValue }) => {
                        const val = getFieldValue("avatar");
                        if (!val) return null;
                        return (
                          <div style={{ position: "relative", marginTop: 8 }}>
                            <Image
                              src={`https://trend-me.com/upload/${val}`}
                              width="100%"
                              style={{ borderRadius: 4 }}
                              preview={{
                                src: `https://trend-me.com/upload/${val}`,
                              }}
                            />
                            <Button
                              type="primary"
                              shape="circle"
                              style={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <UploadOutlined />
                            </Button>
                          </div>
                        );
                      }}
                    </Form.Item>
                  </Col>

                  {/* Couverture */}
                  <Col span={8}>
                    <Form.Item
                      name="couverture"
                      label="Image de couverture"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <Upload
                        customRequest={(options) =>
                          handleFileUpload(options, "couverture")
                        }
                        beforeUpload={beforeUpload}
                        showUploadList={false}
                      >
                        <Button>
                          <UploadOutlined />
                          Télécharger la couverture
                        </Button>
                      </Upload>
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                        prev.couverture !== curr.couverture
                      }
                    >
                      {({ getFieldValue }) => {
                        const val = getFieldValue("couverture");
                        if (!val) return null;
                        return (
                          <div style={{ position: "relative", marginTop: 8 }}>
                            <Image
                              src={`https://trend-me.com/upload/${val}`}
                              width="100%"
                              style={{ borderRadius: 4 }}
                              preview={{
                                src: `https://trend-me.com/upload/${val}`,
                              }}
                            />
                            <Button
                              type="primary"
                              shape="circle"
                              style={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <UploadOutlined />
                            </Button>
                          </div>
                        );
                      }}
                    </Form.Item>
                  </Col>

                  {/* Multimédia (multiple images) */}
                  <Col span={8}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                        prev.multimedia !== curr.multimedia
                      }
                    >
                      {({ getFieldValue }) => {
                        const raw = getFieldValue("multimedia") || [];
                        const fileList = raw.map((file) =>
                          typeof file === "string"
                            ? {
                                uid: `existing-${file}`,
                                url: file,
                                status: "done",
                              }
                            : file
                        );
                        return (
                          <Form.Item
                            name="multimedia"
                            label="Multimédia (images uniquement)"
                            valuePropName="fileList"
                            getValueFromEvent={(e) =>
                              Array.isArray(e)
                                ? e
                                : e.fileList.map((f) => ({
                                    uid: f.uid,
                                    name: f.response?.filename,
                                    status: f.status,
                                    url: f.response?.filename
                                      ? `https://trend-me.com/upload/${f.response.filename}`
                                      : f.url,
                                  }))
                            }
                          >
                            <Upload
                              multiple
                              listType="picture-card"
                              fileList={fileList}
                              customRequest={(options) =>
                                handleFileUpload(options, "multimedia")
                              }
                              beforeUpload={beforeUpload}
                              onRemove={(file) => {
                                const newList = fileList.filter(
                                  (item) => item.uid !== file.uid
                                );
                                form.setFieldsValue({ multimedia: newList });
                              }}
                            >
                              {fileList.length >= 5 ? null : (
                                <div>
                                  <PlusOutlined />
                                  <div style={{ marginTop: 8 }}>Ajouter</div>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Réseaux sociaux */}
              <Panel header="Réseaux sociaux" key="reseaux-sociaux" forceRender>
                <Form.List name="solialMedia">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...rest }) => (
                        <Row key={key} gutter={16} align="middle">
                          <Col span={8}>
                            <Form.Item
                              {...rest}
                              name={[name, "type"]}
                              label="Type"
                              rules={[
                                {
                                  required: true,
                                  message: "Champ obligatoire",
                                },
                              ]}
                            >
                              <Select>
                                <Option value="Tiktok">Tiktok</Option>
                                <Option value="instagram">Instagram</Option>
                                <Option value="GoogleMaps">GoogleMaps</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={14}>
                            <Form.Item
                              {...rest}
                              name={[name, "link"]}
                              label="URL"
                              rules={[
                                {
                                  required: true,
                                  message: "Champ obligatoire",
                                },
                              ]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button danger onClick={() => remove(name)}>
                              Supprimer
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Ajouter un réseau social
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Panel>
            </Collapse>
          </Panel>

          <Panel header="Campagnes" key="campagnes" forceRender>
            <Form.Item
              shouldUpdate={(prev, curr) => prev.campagnes !== curr.campagnes}
            >
              {({ getFieldValue }) => (
                <CampaignTable
                  campagnes={getFieldValue("campagnes") || []}
                  onEdit={handleEditCampaign}
                  onDelete={handleDeleteCampaign}
                  handleAddCampaign={handleAddCampaign}
                />
              )}
            </Form.Item>
          </Panel>

          <CampaignModal
            visible={campaignModalVisible}
            onCancel={() => setCampaignModalVisible(false)}
            onFinish={handleCampaignSubmit}
            initialValues={editingCampaign}
          />
        </Collapse>
      </Form>
    </Modal>
  );
};

// Separate CampaignModal component
const CampaignModal = ({ visible, onCancel, onFinish, initialValues }) => {
  const [form] = Form.useForm();
  const [showRecomponseImmediate, setShowRecomponseImmediate] = useState(false);
  const [starterItems, setStarterItems] = useState([]);
  const [trendyItems, setTrendyItems] = useState([]);
  const [proItems, setProItems] = useState([]);
  const [superStarItems, setSuperStarItems] = useState([]);
  useEffect(() => {
    if (initialValues) {
      // Convert nested recompenseOption arrays to state
      setStarterItems(initialValues.recompenseOption?.starterCategory || []);
      setTrendyItems(initialValues.recompenseOption?.trendyCategory || []);
      setProItems(initialValues.recompenseOption?.proCategory || []);
      setSuperStarItems(
        initialValues.recompenseOption?.superStarCategory || []
      );

      form.setFieldsValue({
        ...initialValues,
        dateDebut: moment(initialValues.dateDebut),
        dateFin: moment(initialValues.dateFin),
        recompenseOption: {
          ...initialValues.recompenseOption,
          // Convert number fields to strings if needed
          starterPoints:
            initialValues.recompenseOption?.starterPoints?.toString(),
          trendyPoints:
            initialValues.recompenseOption?.trendyPoints?.toString(),
          proPoints: initialValues.recompenseOption?.proPoints?.toString(),
          superStarPoints:
            initialValues.recompenseOption?.superStarPoints?.toString(),
        },
      });
      setShowRecomponseImmediate(!!initialValues.recomponseImmediate);
    } else {
      form.resetFields();
      setStarterItems([]);
      setTrendyItems([]);
      setProItems([]);
      setSuperStarItems([]);
      setShowRecomponseImmediate(false);
    }
  }, [form, initialValues, visible]);

  useEffect(() => {
    const sumValue = starterItems.reduce(
      (acc, cur) => acc + (Number(cur?.value) || 0),
      0
    );
    const sumReal = starterItems.reduce(
      (acc, cur) => acc + (Number(cur?.realValue) || 0),
      0
    );
    form.setFieldsValue({
      recompenseOption: {
        ...form.getFieldValue("recompenseOption"),
        starterValue: sumValue,
        starterRealValue: sumReal,
      },
    });
  }, [starterItems]);

  useEffect(() => {
    const sumValue = trendyItems.reduce(
      (acc, cur) => acc + (Number(cur?.value) || 0),
      0
    );
    const sumReal = trendyItems.reduce(
      (acc, cur) => acc + (Number(cur?.realValue) || 0),
      0
    );
    form.setFieldsValue({
      recompenseOption: {
        ...form.getFieldValue("recompenseOption"),
        trendyValue: sumValue,
        trendyRealValue: sumReal,
      },
    });
  }, [trendyItems]);

  useEffect(() => {
    const sumValue = proItems.reduce(
      (acc, cur) => acc + (Number(cur?.value) || 0),
      0
    );
    const sumReal = proItems.reduce(
      (acc, cur) => acc + (Number(cur?.realValue) || 0),
      0
    );
    form.setFieldsValue({
      recompenseOption: {
        ...form.getFieldValue("recompenseOption"),
        proValue: sumValue,
        proRealValue: sumReal,
      },
    });
  }, [proItems]);

  useEffect(() => {
    const sumValue = superStarItems.reduce(
      (acc, cur) => acc + (Number(cur?.value) || 0),
      0
    );
    const sumReal = superStarItems.reduce(
      (acc, cur) => acc + (Number(cur?.realValue) || 0),
      0
    );
    form.setFieldsValue({
      recompenseOption: {
        ...form.getFieldValue("recompenseOption"),
        superStarValue: sumValue,
        superStarRealValue: sumReal,
      },
    });
  }, [superStarItems]);
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        dateDebut: moment(initialValues.dateDebut),
        dateFin: moment(initialValues.dateFin),
      });
      setShowRecomponseImmediate(!!initialValues.recomponseImmediate);
    } else {
      form.resetFields();
    }
  }, [initialValues, visible]);

  const handleValuesChange = (changedValues, allValues) => {
    const recompenseOption = allValues.recompenseOption || {};

    const updateRewardLevel = (level, setItems) => {
      const list =
        recompenseOption[`${level}Category`] &&
        !isNil(recompenseOption[`${level}Category`])
          ? recompenseOption[`${level}Category`]
          : [];

      setItems(list);

      const sumValue = list.reduce(
        (acc, cur) => acc + (Number(cur?.value) || 0),
        0
      );

      const sumReal = list.reduce(
        (acc, cur) => acc + (Number(cur?.realValue) || 0),
        0
      );

      form.setFieldsValue({
        recompenseOption: {
          ...recompenseOption,
          [`${level}Value`]: sumValue,
          [`${level}RealValue`]: sumReal,
        },
      });
    };

    if (changedValues.recompenseOption?.starterCategory !== undefined) {
      updateRewardLevel("starter", setStarterItems);
    }
    if (changedValues.recompenseOption?.trendyCategory !== undefined) {
      updateRewardLevel("trendy", setTrendyItems);
    }
    if (changedValues.recompenseOption?.proCategory !== undefined) {
      updateRewardLevel("pro", setProItems);
    }
    if (changedValues.recompenseOption?.superStarCategory !== undefined) {
      updateRewardLevel("superStar", setSuperStarItems);
    }
  };
  return (
    <Modal
      title={`${initialValues ? "Modifier" : "Créer"} Campagne`}
      visible={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      width={1400}
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        onValuesChange={handleValuesChange}
      >
        <Collapse defaultActiveKey={["campagne"]}>
          {/* Campagne */}
          <Panel header="Campagne" key="campagne" forceRender>
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  name="name"
                  label="name de campagne"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="type"
                  label="Type de campagne"
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Option value="Instagram">Instagram</Option>
                    <Option value="TikTok">TikTok</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="dateDebut"
                  label="Date de début"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="dateFin"
                  label="Date de fin"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name={["recompenseOption", "useRealOrPurchasePrice"]}
                  label="Condition de budget"
                  rules={[
                    { required: true, message: "Ce champ est obligatoire" },
                  ]}
                >
                  <Select
                    placeholder="Sélectionnez le type de valeur"
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="real">
                      Coût (coût d'acquisition)
                    </Select.Option>
                    <Select.Option value="vente">
                      Valeur de vente (prix du marché)
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="budget"
                  label="Budget total (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="mentionsHashTags"
                  label="Mentions et hashtags (séparés par des virgules)"
                >
                  <Input placeholder="#exemple, #hashtag, @name" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="nbfollowersMin"
                  label="Nombre minimal d’abonnés requis pour participer"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="nbhoursMin" label="Durée Scoring">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "participationPerUser"]}
                  label="Participation par utilisateur"
                  rules={[
                    { required: true, message: "Ce champ est obligatoire" },
                  ]}
                >
                  <Select
                    placeholder="Sélectionnez la fréquence"
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="once">1 fois</Select.Option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <Select.Option key={i + 1} value={i + 1}>
                        1 fois chaque {i + 1} mois
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="recomponseImmediate"
              label="Récompense immédiate"
              valuePropName="checked"
            >
              <Switch
                onChange={(checked) => {
                  setShowRecomponseImmediate(checked);
                  if (!checked) {
                    form.setFieldsValue({
                      recomponseImmediate: null,
                      recomponseImmediateValue: null,
                      recomponseImmediateRealValue: null,
                    });
                  }
                }}
              />
            </Form.Item>

            {showRecomponseImmediate && (
              <>
                <Form.Item
                  name="recomponseImmediate"
                  label="Description récompense immédiate"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <TextArea rows={2} />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="recomponseImmediateValue"
                      label="Valeur (€)"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="recomponseImmediateRealValue"
                      label="Valeur Réel (€)"
                      rules={[{ required: true, message: "Champ obligatoire" }]}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="recomponseImmediatenbOfDaysforAvailability"
                      label="Disponibilité de la recompense immediate"
                      rules={[
                        {
                          required: true,
                          message: "Ce champ est obligatoire",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Sélectionnez le type de valeur"
                        style={{ width: "100%" }}
                        allowClear
                      >
                        <Select.Option value="0">immediate</Select.Option>
                        <Select.Option value="1">
                          apres 1 jour de validation de poste
                        </Select.Option>
                        <Select.Option value="2">
                          apres 2 jour de validation de poste
                        </Select.Option>
                        <Select.Option value="3">
                          apres 3 jour de validation de poste
                        </Select.Option>
                        <Select.Option value="4">
                          apres 4 jour de validation de poste
                        </Select.Option>
                        <Select.Option value="5">
                          apres 5 jour de validation de poste
                        </Select.Option>
                        <Select.Option value="6">
                          apres 6 jour de validation de poste
                        </Select.Option>
                        <Select.Option value="7">
                          apres 7 jour de validation de poste
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Panel>

          {/* Système de points */}
          <Panel header="Système de points" key="scoring" forceRender>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={["scoring", "like"]}
                  label="Points like"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["scoring", "comment"]}
                  label="Points commentaire"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["scoring", "followers"]}
                  label="Points abonnés"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          <Panel header="Niveaux de récompense" key="recompenses" forceRender>
            <Divider orientation="center">
              <strong>Disponibilité des récompenses</strong>
            </Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={["recompenseOption", "challengeDescription"]}
                  label="Description de challenge"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <TextArea rows={1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["recompenseOption", "nbOfDaystoBeValdiated"]}
                  label="Disponible après (jours depuis validation de poste)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["recompenseOption", "nbOfDaysforAvailability"]}
                  label="Disponible pendant (jours)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/* === Niveau Starter === */}
            <Divider orientation="left">Niveau Starter</Divider>
            <Row gutter={16}>
              {/* Points requis */}
              <Col span={3}>
                <Form.Item
                  name={["recompenseOption", "starterPoints"]}
                  label="Points requis"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Texte Descriptif Application */}
              <Col span={9}>
                <Form.Item
                  name={["recompenseOption", "starterTitle"]}
                  label="Texte Descriptif Application"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Valeur agrégée (calcul automatique) */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "starterValue"]}
                  label="Valeur (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Coût agrégée */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "starterRealValue"]}
                  label="Coût (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Condition */}
            </Row>

            {/* === Liste dynamique des Nature Récompenses / valeurs / valeurs réelles === */}
            <Form.List name={["recompenseOption", "starterCategory"]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="middle">
                      {/* Nature Récompense (select) */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "category"]}
                          label="Nature Récompense"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <Select placeholder="Choisir">
                            {REWARD_CATEGORIES.map((c) => (
                              <Select.Option key={c} value={c}>
                                {c}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Nom de la récompense"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Valeur */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "value"]}
                          label="Valeur (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Coût */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "realValue"]}
                          label="Coût Réel (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "starterCondition"]}
                          label="Condition d'attribution"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Select
                            placeholder="Sélectionner une condition"
                            style={{ width: "100%" }}
                          >
                            <Select.Option value="minimum d'achat">
                              Minimum d'achat
                            </Select.Option>
                            <Select.Option value="autre">Autre</Select.Option>
                            <Select.Option value="aucune">Aucune</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      {/* Commentaire */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "starterConditionValue"]}
                          label="Commentaire"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Bouton retirer */}
                      <Col span={2}>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ fontSize: 20, color: "red" }}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Ajouter une ligne */}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Ajouter une récompense
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Divider orientation="left">Niveau Trendy</Divider>
            <Row gutter={16}>
              {/* Points requis */}
              <Col span={3}>
                <Form.Item
                  name={["recompenseOption", "trendyPoints"]}
                  label="Points requis"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Texte Descriptif Application */}
              <Col span={9}>
                <Form.Item
                  name={["recompenseOption", "trendyTitle"]}
                  label="Texte Descriptif Application"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Valeur agrégée (calcul automatique) */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "trendyValue"]}
                  label="Valeur (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Coût agrégée */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "trendyRealValue"]}
                  label="Coût (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/* === Liste dynamique des Nature Récompenses / valeurs / valeurs réelles === */}
            <Form.List name={["recompenseOption", "trendyCategory"]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="middle">
                      {/* Nature Récompense (select) */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "category"]}
                          label="Nature Récompense"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <Select placeholder="Choisir">
                            {REWARD_CATEGORIES.map((c) => (
                              <Select.Option key={c} value={c}>
                                {c}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Nom de la récompense"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Valeur */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "value"]}
                          label="Valeur (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Coût */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "realValue"]}
                          label="Coût Réel (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "trendyCondition"]}
                          label="Condition d'attribution"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Select
                            placeholder="Sélectionner une condition"
                            style={{ width: "100%" }}
                          >
                            <Select.Option value="minimum d'achat">
                              Minimum d'achat
                            </Select.Option>
                            <Select.Option value="autre">Autre</Select.Option>
                            <Select.Option value="aucune">Aucune</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      {/* Commentaire */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "trendyConditionValue"]}
                          label="Commentaire"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Bouton retirer */}
                      <Col span={2}>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ fontSize: 20, color: "red" }}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Ajouter une ligne */}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Ajouter une récompense
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Divider orientation="left">Niveau Pro</Divider>
            <Row gutter={16}>
              {/* Points requis */}
              <Col span={3}>
                <Form.Item
                  name={["recompenseOption", "proPoints"]}
                  label="Points requis"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Texte Descriptif Application */}
              <Col span={9}>
                <Form.Item
                  name={["recompenseOption", "proTitle"]}
                  label="Texte Descriptif Application"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Valeur agrégée (calcul automatique) */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "proValue"]}
                  label="Valeur (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Coût agrégée */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "proRealValue"]}
                  label="Coût (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/* === Liste dynamique des Nature Récompenses / valeurs / valeurs réelles === */}
            <Form.List name={["recompenseOption", "proCategory"]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="middle">
                      {/* Nature Récompense (select) */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "category"]}
                          label="Nature Récompense"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <Select placeholder="Choisir">
                            {REWARD_CATEGORIES.map((c) => (
                              <Select.Option key={c} value={c}>
                                {c}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Nom de la récompense"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Valeur */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "value"]}
                          label="Valeur (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Coût */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "realValue"]}
                          label="Coût Réel (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "proCondition"]}
                          label="Condition d'attribution"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Select
                            placeholder="Sélectionner une condition"
                            style={{ width: "100%" }}
                          >
                            <Select.Option value="minimum d'achat">
                              Minimum d'achat
                            </Select.Option>
                            <Select.Option value="autre">Autre</Select.Option>
                            <Select.Option value="aucune">Aucune</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      {/* Commentaire */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "proConditionValue"]}
                          label="Commentaire"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Bouton retirer */}
                      <Col span={2}>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ fontSize: 20, color: "red" }}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Ajouter une ligne */}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Ajouter une récompense
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Divider orientation="left">Niveau SuperStar</Divider>
            <Row gutter={16}>
              {/* Points requis */}
              <Col span={3}>
                <Form.Item
                  name={["recompenseOption", "superStarPoints"]}
                  label="Points requis"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Texte Descriptif Application */}
              <Col span={9}>
                <Form.Item
                  name={["recompenseOption", "superStarTitle"]}
                  label="Texte Descriptif Application"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Valeur agrégée (calcul automatique) */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "superStarValue"]}
                  label="Valeur (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              {/* Total Coût agrégée */}
              <Col span={6}>
                <Form.Item
                  name={["recompenseOption", "superStarRealValue"]}
                  label="Coût (€)"
                  rules={[{ required: true, message: "Champ obligatoire" }]}
                >
                  <Input readOnly style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/* === Liste dynamique des Nature Récompenses / valeurs / valeurs réelles === */}
            <Form.List name={["recompenseOption", "superStarCategory"]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="middle">
                      {/* Nature Récompense (select) */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "category"]}
                          label="Nature Récompense"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <Select placeholder="Choisir">
                            {REWARD_CATEGORIES.map((c) => (
                              <Select.Option key={c} value={c}>
                                {c}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Nom de la récompense"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Valeur */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "value"]}
                          label="Valeur (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Coût */}
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, "realValue"]}
                          label="Coût Réel (€)"
                          rules={[
                            {
                              required: true,
                              message: "Champ obligatoire",
                            },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "superStarCondition"]}
                          label="Condition d'attribution"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Select
                            placeholder="Sélectionner une condition"
                            style={{ width: "100%" }}
                          >
                            <Select.Option value="minimum d'achat">
                              Minimum d'achat
                            </Select.Option>
                            <Select.Option value="autre">Autre</Select.Option>
                            <Select.Option value="aucune">Aucune</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      {/* Commentaire */}
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "superStarConditionValue"]}
                          label="Commentaire"
                          rules={[
                            { required: true, message: "Champ obligatoire" },
                          ]}
                        >
                          <Input style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>

                      {/* Bouton retirer */}
                      <Col span={2}>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ fontSize: 20, color: "red" }}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Ajouter une ligne */}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Ajouter une récompense
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Panel>
        </Collapse>
      </Form>
    </Modal>
  );
};

export default EtablissementModal;
